import { FC, useEffect, useState } from 'react';
import Estimates from './Estimates';
import EstimateModal from 'components/estimate-modal/EstimateModal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import StripeSubscriptionsModal from './components/StripeSubscriptionsModal';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectCurrentTeam, selectIsActionsBlocked, showNotifier } from 'containers/app/appSlice';
import { SubscriptionStatus } from 'containers/app/app.model';
import {
  useCompleteSubscriptionMutation,
  useGetSubscriptionPlansQuery,
  useInitiateSubscriptionMutation,
  useLazyGetCheckoutSessionQuery
} from './api';
import { InitiateSubscriptionPayload } from './estimates.model';
import { CheckoutSessionStatus, SubscriptionDescription, SubscriptionProcessingStatus } from './constants';
import EstimateSubscribedModal from './components/EstimateSubscribedModal';
import routesPath from 'routes/RoutesPath';
import { useCommunicationsControl } from 'containers/communications';
import { useLazyGetMerchantTeamsQuery } from 'containers/app/api';
import { NotifierTypes } from 'constants/notifierConstants';
import BlockedInfoPopup from 'containers/home/components/BlockedInfoPopup';

const EstimatesLandingPage: FC = () => {
  const navigate = useNavigate();
  const { allowAction: allowEstimateCreate } = useCommunicationsControl('ESTIMATE_CREATE');
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const isRedirected = searchParams.get('redirect');
  const sessionId = searchParams.get('session_id');

  const isActionsBlocked = useAppSelector(selectIsActionsBlocked);
  const { subscriptionStatus, id: teamId } = useAppSelector(selectCurrentTeam);
  const { data: subscriptionPlans } = useGetSubscriptionPlansQuery();
  const [initiateSubscription, { isLoading: isInitiatingSubscription }] = useInitiateSubscriptionMutation();
  const [getCheckoutSession] = useLazyGetCheckoutSessionQuery();
  const [completeSubscription] = useCompleteSubscriptionMutation();
  const [getMerchantTeams] = useLazyGetMerchantTeamsQuery();

  const { name, id, trialDuration, price } =
    subscriptionPlans?.data?.find(item => item.description === SubscriptionDescription.ESTIMATE_ONLY) ?? {};

  const [openStripeSubscriptionModal, setOpenStripeSubscriptionModal] = useState(false);
  const [openEstimateModal, setOpenEstimateModal] = useState(
    subscriptionStatus === SubscriptionStatus.NOT_SUBSCRIBED && !isRedirected
  );
  const [openEstimateSubscribedModal, setOpenEstimateSubscribedModal] = useState(false);
  const [showMerchantBlockedInfo, setShowMerchantBlockedInfo] = useState(false);

  const getCheckoutSessionData = async (sessionIdParam: string) => {
    const data = await getCheckoutSession({ teamId, sessionId: sessionIdParam }).unwrap();
    const status = data?.data?.status;
    const completeSubscriptionPayload: InitiateSubscriptionPayload = {
      subscriptionPlanId: id,
      subscriptionPlanName: name,
      type: 'WEB',
      status:
        status === CheckoutSessionStatus.COMPLETE
          ? SubscriptionProcessingStatus.PURCHASED
          : SubscriptionProcessingStatus.ERROR
    };
    await completeSubscription({ teamId, payload: completeSubscriptionPayload });
    if (status === CheckoutSessionStatus.COMPLETE) {
      setOpenEstimateSubscribedModal(true);
    } else {
      setOpenEstimateModal(true);
    }
  };

  useEffect(() => {
    if (isRedirected && id && teamId) {
      getCheckoutSessionData(sessionId);
      getMerchantTeams();
      navigate(routesPath.ESTIMATES, { replace: true });
    }
  }, [isRedirected, id, teamId]);

  useEffect(() => {
    setOpenEstimateModal(subscriptionStatus === SubscriptionStatus.NOT_SUBSCRIBED && !isRedirected);
  }, [teamId]);

  const handleOpenStripeSubscriptionModal = async () => {
    if (isActionsBlocked) {
      setShowMerchantBlockedInfo(true);
      return;
    }
    const initiateSubscriptionPayload: InitiateSubscriptionPayload = {
      subscriptionPlanId: id,
      subscriptionPlanName: name,
      type: 'WEB',
      status: SubscriptionProcessingStatus.INITIATED
    };
    try {
      await initiateSubscription({ teamId, payload: initiateSubscriptionPayload });
      setOpenStripeSubscriptionModal(true);
      setOpenEstimateModal(false);
    } catch (error) {
      dispatch(
        showNotifier({
          type: NotifierTypes.ERROR,
          message: {
            primaryMessage: 'Something went wrong. Please try again after some time.',
            isMessageHtml: false
          }
        })
      );
    }
  };

  const handleCloseEstimateSubscribedModal = () => {
    setOpenEstimateSubscribedModal(false);
  };

  const handleCreateNewEstimate = () => {
    if (!allowEstimateCreate()) return;
    navigate(routesPath.ESTIMATES_CREATE);
  };

  const handleCloseStripeSubscriptionModal = async (sessionIdParam: string) => {
    getCheckoutSessionData(sessionIdParam);
    setOpenStripeSubscriptionModal(false);
  };

  const handleOpenSubscribeToEstimateModal = () => {
    setOpenEstimateModal(true);
  };

  return (
    <div className="relative h-full w-full">
      <div
        className={
          openEstimateModal
            ? 'pointer-events-none h-full overflow-hidden'
            : 'customNormalScroll h-[calc(100%_-_80px)]  overflow-y-auto'
        }>
        <Estimates
          estimateSubscriptionPrice={price}
          handleOpenSubscribeToEstimateModal={handleOpenSubscribeToEstimateModal}
        />
      </div>
      {openEstimateModal && (
        <div className="absolute top-0 left-0 h-full w-full bg-black/5 backdrop-blur-sm">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <EstimateModal
              onTryFree={handleOpenStripeSubscriptionModal}
              trialDuration={trialDuration}
              price={price}
              isLoading={isInitiatingSubscription}
            />
          </div>
        </div>
      )}
      {openStripeSubscriptionModal && (
        <StripeSubscriptionsModal
          handleClose={handleCloseStripeSubscriptionModal}
          isFreeTrial={true}
          subscriptionPlanName={name}
        />
      )}
      {openEstimateSubscribedModal && (
        <EstimateSubscribedModal
          onClose={handleCloseEstimateSubscribedModal}
          handleCreateNewEstimate={handleCreateNewEstimate}
        />
      )}
      {showMerchantBlockedInfo && (
        <BlockedInfoPopup isOpen={showMerchantBlockedInfo} onClose={() => setShowMerchantBlockedInfo(false)} />
      )}
    </div>
  );
};

export default EstimatesLandingPage;
